/**
 * formDeriveValuesMatch
 *
 * Decides whether a stored derive output is already correct — the guard that
 * keeps the re-entrant pass from writing. Strict deep equality with NO numeric
 * tolerance, unlike `arFormulaValuesMatch`, whose 1e-9 band absorbs pre-engine
 * writers that derives do not have. `null` and `undefined` are equal, so a blank
 * value written as literal `null` never oscillates against an absent cell — and
 * that same equivalence holds at EVERY depth, over the union of each side's
 * defined keys, because a member Firestore dropped for being `undefined` must
 * read as equal to the member the engine recomputed.
 * Numbers use `===` plus an explicit both-NaN case, NOT `Object.is`: `Object.is`
 * separates `-0` from `0`, which Firestore does not preserve, so it would order
 * a rewrite of the same value on every pass, forever.
 */

import { isRecordValue } from './isRecordValue';
import { isTimestampShaped } from './isTimestampShaped';

/**
 * The keys that can carry a difference. A member set to `undefined` is not one:
 * Firestore runs with `ignoreUndefinedProperties`, so it is never persisted and
 * comes back absent. Comparing raw key-set LENGTH would therefore read a computed
 * `{value: undefined}` as different from the stored `{}` it produces, on every
 * pass, forever — the write loop this whole guard exists to end.
 */
function definedKeys(value: Record<string, unknown>): string[] {
  return Object.keys(value).filter((key) => value[key] !== undefined);
}

function formDeriveValuesMatch(computed: unknown, stored: unknown): boolean {
  if (computed === null || computed === undefined) {
    return stored === null || stored === undefined;
  }
  if (stored === null || stored === undefined) return false;
  if (typeof computed !== 'object' || typeof stored !== 'object') {
    if (typeof computed === 'number' && typeof stored === 'number') {
      return computed === stored || (Number.isNaN(computed) && Number.isNaN(stored));
    }
    return computed === stored;
  }
  if (isTimestampShaped(computed) || isTimestampShaped(stored)) {
    return (
      isTimestampShaped(computed) &&
      isTimestampShaped(stored) &&
      computed.seconds === stored.seconds &&
      computed.nanoseconds === stored.nanoseconds
    );
  }
  if (Array.isArray(computed) || Array.isArray(stored)) {
    return (
      Array.isArray(computed) &&
      Array.isArray(stored) &&
      computed.length === stored.length &&
      computed.every((item, index) => formDeriveValuesMatch(item, stored[index]))
    );
  }
  if (!isRecordValue(computed) || !isRecordValue(stored)) return false;
  return [...definedKeys(computed), ...definedKeys(stored)].every((key) =>
    formDeriveValuesMatch(computed[key], stored[key]),
  );
}

export { formDeriveValuesMatch };
